<template>
  <main class="page">
    <!-- Hero -->
    <header class="hero">
      <p class="eyebrow">Privacy Policy</p>
      <h1>Vi-Link In-Vehicle Connectivity Privacy Policy</h1>
      <p class="subtitle">Developer: {{ company.name }}</p>
      <div class="meta"
           aria-label="Policy dates">
        <span>Effective date: {{ company.effectiveDate }}</span>
        <span>Last updated: {{ company.lastUpdated }}</span>
      </div>
    </header>

    <article class="policy">
      <!-- Summary -->
      <div class="summary">
        <p>
          <strong>Policy Summary:</strong> This App does not require account
          registration, display advertisements, or use analytics or
          advertising-tracking SDKs. To provide in-vehicle connectivity, device
          discovery, screen casting, and built-in browser features, the App may,
          after obtaining the required authorization, access nearby-device
          information, network status, screen content, and webpage information
          that the user actively enters or saves. Except when the user chooses
          to visit a third-party website or cast content to a connected
          in-vehicle device, this data is not sent to the Developer's servers.
        </p>
      </div>

      <!-- 数据驱动条款 -->
      <section v-for="section in sections"
               :key="section.id"
               :id="section.id"
               class="policy-section">
        <h2>{{ section.title }}</h2>

        <template v-for="(block, i) in section.blocks">
          <p v-if="block.type === 'p'"
             :key="'p-' + i"
             v-html="block.html"></p>

          <ul v-else-if="block.type === 'ul'"
              :key="'ul-' + i">
            <li v-for="(item, j) in block.items"
                :key="'li-' + j"
                v-html="item"></li>
          </ul>

          <div v-else-if="block.type === 'note'"
               :key="'note-' + i"
               class="note">
            <p v-html="block.html"></p>
          </div>
        </template>
      </section>

      <!-- 联系信息（特殊样式） -->
      <section id="contact"
               class="policy-section">
        <h2>10. Contact Us</h2>
        <div class="contact">
          <p><strong>Developer:</strong> {{ company.name }}</p>
          <p>
            <strong>Email:</strong>
            <a :href="mailtoHref">{{ company.email }}</a>
          </p>
          <p>
            If you have questions, comments, complaints, or deletion requests
            concerning this Policy or our processing of personal information,
            please contact us using the method above. After verifying your
            request, we will process it and respond in accordance with
            applicable laws and regulations.
          </p>
        </div>
      </section>
    </article>

    <footer class="site-footer">
      © {{ company.copyrightYear }} {{ company.name }}
    </footer>
  </main>
</template>

<script>
export default {
  name: 'App',

  data () {
    const company = {
      name: 'SunMedia Technology Co., Ltd.',
      email: 'support@sunmedia.com.cn',
      effectiveDate: 'September 10, 2026',
      lastUpdated: 'September 10, 2026',
      copyrightYear: 2026
    }

    const sections = [
      {
        id: 'scope',
        title: '1. Scope and Developer',
        blocks: [
          {
            type: 'p',
            html: `This Privacy Policy applies to Vi-Link In-Vehicle Connectivity (the “App”), provided by ${company.name} (“we,” “us,” or “our”). The App connects compatible mobile devices and in-vehicle devices via USB, Wi-Fi Direct, or similar methods and provides screen casting and built-in web-browsing features.`
          },
          {
            type: 'p',
            html: 'This Policy explains how the App accesses, processes, transmits, stores, and protects user and device data. Please read it carefully before using the App.'
          }
        ]
      },
      {
        id: 'data-access',
        title: '2. Data Access, Use, and Transmission',
        blocks: [
          {
            type: 'p',
            html: "To determine device compatibility, select the appropriate implementation of system features, and adapt the user interface, the App reads the device model, Android version, and basic runtime-environment information. This information is processed locally and in real time on the device. It is neither sent to nor stored on the Developer's servers."
          },
          {
            type: 'p',
            html: "To discover, display, and connect to a compatible in-vehicle device selected by the user, and to establish and maintain the in-vehicle connection, the App accesses the names, device addresses, device categories, connection status, and network status of nearby Wi-Fi Direct or Bluetooth devices. This information is processed only on the device and between the device and the nearby device selected by the user, solely for the current discovery or connection process. It is neither sent to the Developer's servers nor stored by us on any server."
          },
          {
            type: 'p',
            html: 'Some Android versions classify nearby-device discovery through Wi-Fi Direct or Bluetooth as requiring location permission. The App therefore uses location permission for nearby-device discovery and checks whether system location services are enabled. <strong>The App does not read, store, or transmit your geographic coordinates, movement history, or actual location, and it does not retain any location data.</strong>'
          },
          {
            type: 'p',
            html: "When the user actively starts screen casting and confirms Android's system screen-capture authorization, the App transmits the screen content the user chooses to share in real time to the connected in-vehicle device through the current USB or local direct-device connection. Screen content is processed in real time only while casting. It is neither uploaded to the Developer's servers nor stored by us."
          },
          {
            type: 'p',
            html: "To provide the built-in browser, open webpages, and manage bookmarks actively saved by the user, the App processes webpage addresses, webpage titles, website icons, bookmark status, and browser settings. Bookmarks and settings remain on the device until the user deletes the bookmarks, clears the App's data, or uninstalls the App. When the user visits a website, Android System WebView sends requests to that website as part of the normal operation of the internet."
          },
          {
            type: 'p',
            html: "Third-party websites visited by the user may use cookies, website storage, or caches to maintain sign-in status, preserve sessions, and provide webpage features. They may also process data that the user actively submits to them. Such data is processed directly by Android System WebView and the applicable website, is governed by that website's own privacy policy, and is not received by us. Retention periods are determined by the website and WebView settings. Users can delete this data by clearing the App's data or uninstalling the App."
          },
          {
            type: 'p',
            html: "The App may record crash logs and diagnostic information locally on the device, including exception stack traces and App runtime status, to help diagnose faults. This information is written only to the App's cache directory and is not automatically uploaded to us or any third party. It is deleted when the system clears the cache, the user clears the App's cache or data, or the user uninstalls the App."
          },
          {
            type: 'p',
            html: "To remember user choices and provide the corresponding features, the App locally stores the user's Privacy Policy consent status and preferences related to casting methods, display, and disconnection. This information is not sent to the Developer's servers and remains until the user clears the App's data or uninstalls the App."
          }
        ]
      },
      {
        id: 'permissions',
        title: '3. Permissions and System Features',
        blocks: [
          {
            type: 'p',
            html: 'The App uses the following permissions or system capabilities only when providing the relevant features:'
          },
          {
            type: 'ul',
            items: [
              '<strong>Bluetooth and nearby devices:</strong> To scan for, display, connect to, and communicate with compatible in-vehicle devices.',
              '<strong>Wi-Fi and network:</strong> To discover Wi-Fi Direct devices, establish local connections, check and—based on user actions—adjust connection status, and access webpages selected by the user.',
              '<strong>Location:</strong> To satisfy system requirements for Wi-Fi Direct or Bluetooth device discovery on certain Android versions. The App does not obtain geographic coordinates.',
              '<strong>Screen capture and media-projection foreground service:</strong> To continue casting the screen after the user expressly starts the feature and grants Android system authorization; a persistent notification is displayed during casting.',
              '<strong>Notifications:</strong> To show the active screen-casting service and related connection status.',
              '<strong>Display over other apps:</strong> To display a casting- or control-related overlay after the user grants authorization.',
              '<strong>Modify system settings/network status:</strong> To adjust related settings only when required for an in-vehicle connection or display adaptation and in response to user actions. Users can reverse these changes in system settings.',
              '<strong>Ignore battery optimization and keep awake:</strong> After the user actively allows it, to reduce interruptions to casting or connections caused by sleep or background restrictions.',
              '<strong>USB accessory:</strong> To identify and connect to a compatible in-vehicle USB device attached by the user.'
            ]
          },
          {
            type: 'p',
            html: "During the normal flow of the applicable feature, the App explains what data will be accessed and why, and requests the user's explicit consent before accessing personal or sensitive user data that requires authorization. Users may deny a request or withdraw a granted permission at any time in Android system settings. The corresponding feature may then become unavailable, but unrelated features will not be affected."
          },
          {
            type: 'p',
            html: 'Closing a page, pressing the Back button, remaining silent, or continuing to browse is not treated as consent. Screen casting begins only after the user actively starts it and confirms the system screen-capture prompt. The user may stop transmission at any time by stopping the cast, disconnecting, or stopping the foreground service.'
          }
        ]
      },
      {
        id: 'third-party',
        title: '4. Third-Party Websites, Cookies, and Network Requests',
        blocks: [
          {
            type: 'p',
            html: "The App's built-in browser allows users to access the internet. When a user opens a third-party website, that website may receive the user's IP address, browser-request information, and data actively submitted by the user. It may also use cookies or similar technologies to retain sign-in status or preferences. Such processing is determined by the applicable website and governed by its privacy policy. Please review a website's privacy policy before submitting personal information."
          },
          {
            type: 'p',
            html: 'We do not control third-party websites and make no representations or guarantees regarding their data-processing practices. The App itself does not use cookies for user analytics, advertising, or cross-app tracking.'
          }
        ]
      },
      {
        id: 'sharing',
        title: '5. Data Sharing, Sale, and Third-Party SDKs',
        blocks: [
          {
            type: 'p',
            html: "We do not collect the data described in this Policy on the Developer's servers, sell personal or sensitive user data, or share such data with advertisers, data brokers, or analytics providers. The App does not integrate any third-party SDK for advertising, behavioral analytics, or remote crash reporting."
          },
          {
            type: 'p',
            html: "The following user-initiated data transmissions are part of the App's core functionality:"
          },
          {
            type: 'ul',
            items: [
              'During screen casting, screen content is transmitted in real time to the in-vehicle device selected and connected by the user.',
              'When using the built-in browser, webpage requests and content actively submitted by the user are transmitted to the website the user chooses to visit.'
            ]
          }
        ]
      },
      {
        id: 'retention',
        title: '6. Data Retention, Deletion, and User Rights',
        blocks: [
          {
            type: 'p',
            html: 'The App does not offer account creation. Therefore, there are no App accounts or account-associated server data, and no account-deletion process applies.'
          },
          {
            type: 'p',
            html: "Users may delete saved bookmarks through the App's bookmark-management interface; delete caches and local logs using Android's “Clear cache” option; and delete bookmarks, cookies, website storage, settings, consent status, and other local data saved by the App by selecting “Clear storage” or “Clear data” in Android or by uninstalling the App. Users can also review or revoke permissions in Android system settings."
          },
          {
            type: 'p',
            html: 'If you separately submit information to us through the contact channel listed in Section 10, you may contact us to request access, correction, or deletion. Where laws or regulations require retention, we retain the information only for the legally required period and scope, then delete or anonymize it when that period expires.'
          }
        ]
      },
      {
        id: 'security',
        title: '7. Data Security',
        blocks: [
          {
            type: 'p',
            html: "We follow the principle of data minimization and use the Android application sandbox, permission controls, and restricted App storage to protect local data. Screen content is transmitted through the current USB or local-device connection without passing through the Developer's servers. The security of internet transmissions also depends on the websites visited by the user and the transmission protocols they use."
          },
          {
            type: 'p',
            html: 'No storage or transmission method can guarantee absolute security. If we discover a personal-information security incident relating to the App, we will take reasonable response measures in accordance with applicable laws and regulations.'
          }
        ]
      },
      {
        id: 'children',
        title: "8. Children's Privacy",
        blocks: [
          {
            type: 'p',
            html: "The App is intended for general users who need in-vehicle connectivity. It is not specifically directed to children, and we do not knowingly collect children's personal information. If we discover that we have received a child's personal information in error, we will take reasonable steps to delete it promptly as required by law."
          }
        ]
      },
      {
        id: 'updates',
        title: '9. Updates to This Policy',
        blocks: [
          {
            type: 'p',
            html: "If the App's data-processing practices change, we will update this Policy and its “Last updated” date. Before processing personal or sensitive user data in a way that is beyond users' reasonable expectations, we will provide a clear and prominent in-App notice explaining what data is accessed or collected and how it is used and shared, and we will obtain the user's explicit consent where required."
          },
          {
            type: 'p',
            html: "We will also keep the “Data safety” information in Google Play Console aligned with this Policy and the App's actual behavior."
          }
        ]
      }
    ]

    return { company, sections }
  },

  computed: {
    mailtoHref () {
      return 'mailto:' + this.company.email
    }
  }
}
</script>

<style>
/* ---------- 主题变量 ---------- */
:root {
  color-scheme: light dark;
  --bg: #f3f6fa;
  --card: #fff;
  --text: #172033;
  --muted: #5d687b;
  --line: #dce3ed;
  --accent: #1769e0;
  --accent-soft: #eaf2ff;
  --shadow: 0 16px 46px rgba(24, 39, 66, 0.08);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font: 16px/1.75 -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-align: left;
}

a {
  color: var(--accent);
}

a:focus-visible {
  outline: 3px solid var(--accent);
  outline-offset: 3px;
  border-radius: 4px;
}

/* ---------- 布局 ---------- */
.page {
  width: min(940px, calc(100% - 32px));
  margin: 36px auto;
  text-align: left;
}

.hero,
.policy {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: var(--shadow);
  text-align: left;
}

.hero {
  padding: 38px 42px;
  background-image: linear-gradient(
    135deg,
    var(--accent-soft),
    transparent 64%
  );
}

.eyebrow {
  margin: 0 0 6px;
  color: var(--accent);
  font-size: 0.84rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: left;
}

h1 {
  margin: 0;
  font-size: clamp(1.9rem, 5vw, 3rem);
  line-height: 1.22;
  text-align: left;
}

.subtitle {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 1.05rem;
  text-align: left;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  margin-top: 22px;
  color: var(--muted);
  text-align: left;
}

/* ---------- 正文 ---------- */
.policy {
  padding: 34px 42px 42px;
  margin-top: 18px;
  text-align: left;
}

.summary {
  margin-bottom: 26px;
  padding: 18px 20px;
  background: var(--accent-soft);
  border-left: 4px solid var(--accent);
  border-radius: 10px;
  text-align: left;
}

.summary p {
  margin: 0;
  text-align: left;
}

.policy-section h2 {
  margin: 32px 0 9px;
  font-size: 1.28rem;
  line-height: 1.45;
  text-align: left;
}

.policy-section:first-of-type h2 {
  margin-top: 0;
}

p {
  margin: 8px 0 14px;
  text-align: left;
}

ul {
  margin: 8px 0 16px;
  padding-left: 1.45rem;
  text-align: left;
}

li + li {
  margin-top: 7px;
  text-align: left;
}

.note {
  margin: 12px 0 16px;
  padding: 14px 18px;
  background: var(--accent-soft);
  border-left: 3px solid var(--accent);
  border-radius: 0 10px 10px 0;
  font-size: 0.96rem;
  text-align: left;
}

.note p {
  margin: 0;
}

/* ---------- 联系卡片 ---------- */
.contact {
  padding: 18px 20px;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 10px;
  text-align: left;
}

.contact p {
  margin: 4px 0;
  text-align: left;
}

/* ---------- 页脚 ---------- */
.site-footer {
  padding: 24px 12px 8px;
  color: var(--muted);
  text-align: center;
  font-size: 0.9rem;
}

/* ---------- 响应式 ---------- */
@media (max-width: 640px) {
  .page {
    width: calc(100% - 20px);
    margin: 10px auto 24px;
  }

  .hero,
  .policy {
    padding: 25px 22px 30px;
    border-radius: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
}

/* ---------- 深色模式 ---------- */
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0e1420;
    --card: #161f2d;
    --text: #eef3fb;
    --muted: #aeb9ca;
    --line: #303c4f;
    --accent: #83b4ff;
    --accent-soft: #172c49;
    --shadow: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PrivacyPolicy.vue